import type { SuperSessionEntry } from '@lms/api-client/backend';
import {
  CategoryBarChart,
  ChartCard,
  DonutChart,
  formatNumber,
  KpiTile,
  RatingChart,
  StackedTimeChart,
  sumBy,
  useSeries,
} from '@lms/charts';
import { useState } from 'react';
import { useLocale, useTranslations } from 'use-intl';
import { PeriodSelector } from '@/features/dashboard/components/period-selector';
import {
  StatsGrid,
  StatsPlaceholder,
  StatsToolbar,
  usePeriodLabel,
  WIDE,
} from '@/features/dashboard/components/stats-layout';
import {
  type StatsHookParams,
  useSuperStats,
} from '@/features/dashboard/use-super-stats';

/** Backend status strings → `stats.session_status.*` keys. */
const STATUS_KEYS: Record<string, string> = {
  'Not started': 'not_started',
  Started: 'started',
  Completed: 'completed',
};
const customerOf = (s: SuperSessionEntry) => s.portfolioName ?? '–';
const locationOf = (s: SuperSessionEntry) => s.locationName;
const countOf = (s: SuperSessionEntry) => s.count;
const periodOf = (s: SuperSessionEntry) => s.period;

/** Entities with at least this many completed trainings count as active. */
const ACTIVE_THRESHOLD = 10;

export function SuperStats({
  locations,
  customers,
}: {
  locations: number[];
  /** Total number of customers (portfolios), for the KPI denominator. */
  customers: number;
}) {
  const t_stats = useTranslations('stats');
  const locale = useLocale();
  const [filter, setFilter] = useState<StatsHookParams>({
    period: 'year',
    fromDate: new Date(),
  });

  const { questions, sessions, isLoading, error } = useSuperStats(filter);
  const periodLabel = usePeriodLabel(filter.period, filter.fromDate);
  const other = t_stats('other');
  const statusOf = (s: SuperSessionEntry) => {
    const key = STATUS_KEYS[s.finished];
    return key ? t_stats(`session_status.${key}`) : s.finished;
  };

  const statuses = useSeries(sessions, statusOf, countOf, {
    otherLabel: other,
  });
  const customerSeries = useSeries(sessions, customerOf, countOf, {
    otherLabel: other,
  });

  const toolbar = (
    <StatsToolbar>
      <div className="w-full lg:max-w-72">
        <PeriodSelector
          startDate={new Date('2024-01-01T00:00:00+00:00')}
          onChange={(period, fromDate) =>
            setFilter((f) => ({ ...f, fromDate, period }))
          }
        />
      </div>
    </StatsToolbar>
  );

  if (isLoading || error) {
    return (
      <>
        {toolbar}
        <StatsPlaceholder state={error ? 'error' : 'loading'} error={error} />
      </>
    );
  }

  const completed = sessions.filter((s) => s.finished === 'Completed');
  const activeCount = (by: (s: SuperSessionEntry) => string) =>
    [...sumBy(completed, by, countOf).values()].filter(
      (v) => v >= ACTIVE_THRESHOLD,
    ).length;
  const range = {
    period: filter.period,
    fromDate: filter.fromDate ?? new Date(),
  };
  const total = sessions.reduce((sum, s) => sum + s.count, 0);
  const ratings = (['understanding', 'acting', 'explaining'] as const).filter(
    (k) => questions[k].length > 0,
  );

  return (
    <>
      {toolbar}
      <StatsGrid>
        <ChartCard title={t_stats('sessions')} description={periodLabel}>
          <DonutChart
            data={sessions}
            name={statusOf}
            value={countOf}
            seriesSet={statuses}
            centerLabel={t_stats('trainings_lower')}
            locale={locale}
            emptyLabel={t_stats('no_data')}
          />
        </ChartCard>

        <KpiTile
          label={t_stats('customers')}
          description={t_stats('with_ten_trainings')}
          value={activeCount(customerOf)}
          of={customers}
          locale={locale}
        />
        <KpiTile
          label={t_stats('locations')}
          description={t_stats('with_ten_trainings')}
          value={activeCount(locationOf)}
          of={locations.length}
          locale={locale}
        />

        <ChartCard
          title={t_stats('trainings_per_status')}
          description={periodLabel}
          value={formatNumber(total, locale)}
          className={WIDE}
        >
          <StackedTimeChart
            data={sessions}
            period={periodOf}
            series={statusOf}
            value={countOf}
            seriesSet={statuses}
            range={range}
            locale={locale}
            emptyLabel={t_stats('no_data')}
            totalLabel={t_stats('total')}
          />
        </ChartCard>

        <ChartCard
          title={t_stats('trainings_per_customer')}
          description={periodLabel}
          className={WIDE}
        >
          <StackedTimeChart
            data={sessions}
            period={periodOf}
            series={customerOf}
            value={countOf}
            seriesSet={customerSeries}
            range={range}
            locale={locale}
            emptyLabel={t_stats('no_data')}
            totalLabel={t_stats('total')}
          />
        </ChartCard>

        <ChartCard
          title={t_stats('trainings_per_location')}
          description={periodLabel}
          className={WIDE}
        >
          <CategoryBarChart
            data={sessions}
            category={locationOf}
            stack={statusOf}
            value={countOf}
            seriesSet={statuses}
            limit={20}
            otherLabel={other}
            locale={locale}
            labelWidth={180}
            emptyLabel={t_stats('no_data')}
            totalLabel={t_stats('total')}
          />
        </ChartCard>

        {ratings.map((key) => (
          <ChartCard key={key} title={t_stats(key)} description={periodLabel}>
            <RatingChart
              data={questions[key]}
              labels={{
                before: t_stats('before_course'),
                after: t_stats('after_course'),
              }}
              locale={locale}
            />
          </ChartCard>
        ))}
      </StatsGrid>
    </>
  );
}
